import React, { useState } from 'react';
import './DownloadButtons.css';

const DownloadButtons = ({ conversationId, messageContent, requestType, imageCache }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);

  // Show download buttons for any substantial content
  if (!messageContent || messageContent.length < 1000) {
    return null;
  }

  // Function to extract document IDs from content
  const extractDocumentIds = (text) => {
    const documentIds = new Map();
    
    // Pattern 1: (Document ID: xxx)
    const pattern1 = /\(Document ID:\s*([a-f0-9]{24})\)/gi;
    let match;
    while ((match = pattern1.exec(text)) !== null) {
      documentIds.set(match[1], true);
    }
    
    // Pattern 2: document_id: xxx
    const pattern2 = /document_id:\s*([a-f0-9]{24})/gi;
    while ((match = pattern2.exec(text)) !== null) {
      documentIds.set(match[1], true);
    }
    
    return Array.from(documentIds.keys());
  };

  // Function to get image URL for a document ID with authentication
  const getImageUrl = (docId) => {
    const token = localStorage.getItem('token');
    return `/api/documents/${docId}/view?token=${token}`;
  };

  // Function to check if document is a PDF with pages
  const isPdfWithPages = (doc) => {
    return doc && doc.mimetype === 'application/pdf' && doc.pageImages && doc.pageImages.length > 0;
  };

  // Function to get PDF page URL
  const getPdfPageUrl = (docId, pageNumber) => {
    const token = localStorage.getItem('token');
    return `/api/documents/${docId}/page/${pageNumber}?token=${token}`;
  };

  // Function to strip markdown formatting for plain text
  const stripMarkdown = (text) => {
    return text
      // IMPORTANT: Preserve Document IDs before stripping other formatting
      .replace(/\*\*([^*]+?)\s*\(Document ID:\s*([a-f0-9]{24})\)\*\*/gi, '$1 (Document ID: $2)')
      // Remove headers (# ## ###)
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold (**text** or __text__)
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      // Remove italic (*text* or _text_)
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      // Remove code blocks (```code```)
      .replace(/```[\s\S]*?```/g, '')
      // Remove inline code (`code`)
      .replace(/`(.*?)`/g, '$1')
      // Remove links [text](url)
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove bullet points
      .replace(/^[-*+]\s+/gm, '')
      // Remove numbered lists
      .replace(/^\d+\.\s+/gm, '')
      // Clean up extra whitespace
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
  };

  // Function to process content with images for different formats
  const processContentWithImages = (content, format) => {
    const documentIds = extractDocumentIds(content);
    
    if (format === 'txt') {
      // For TXT, just add image URLs at the end
      let processedContent = stripMarkdown(content);
      if (documentIds.length > 0) {
        processedContent += '\n\n--- Image References ---\n';
        documentIds.forEach(docId => {
          const url = getImageUrl(docId);
          const imageInfo = imageCache && imageCache[docId] ? imageCache[docId] : null;
          const filename = imageInfo ? imageInfo.filename : 'image';
          
          // Check if it's a PDF with pages
          if (isPdfWithPages(imageInfo)) {
            processedContent += `\n${filename} (PDF with ${imageInfo.pageImages.length} pages):\n`;
            imageInfo.pageImages.forEach(page => {
              const pageUrl = getPdfPageUrl(docId, page.pageNumber);
              processedContent += `  - Page ${page.pageNumber}: ${window.location.origin}${pageUrl}\n`;
            });
          } else {
            processedContent += `${filename}: ${window.location.origin}${url}\n`;
          }
        });
      }
      return processedContent;
    }
    
    if (format === 'md') {
      // For Markdown, replace document references with markdown image syntax
      let processedContent = content;
      documentIds.forEach(docId => {
        const url = getImageUrl(docId);
        const imageInfo = imageCache && imageCache[docId] ? imageCache[docId] : null;
        const filename = imageInfo ? imageInfo.filename : 'image';
        
        // Check if it's a PDF with pages
        if (isPdfWithPages(imageInfo)) {
          // Replace patterns with PDF pages
          const patterns = [
            new RegExp(`\\*\\*${filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(Document ID:\\s*${docId}\\)\\*\\*`, 'gi'),
            new RegExp(`${filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(Document ID:\\s*${docId}\\)`, 'gi')
          ];
          
          patterns.forEach(pattern => {
            processedContent = processedContent.replace(pattern, (match) => {
              let pdfMarkdown = `${match}\n\n`;
              imageInfo.pageImages.forEach(page => {
                const pageUrl = getPdfPageUrl(docId, page.pageNumber);
                pdfMarkdown += `![${filename} - Page ${page.pageNumber}](${window.location.origin}${pageUrl})\n\n`;
              });
              return pdfMarkdown;
            });
          });
        } else {
          // Regular image handling
          const patterns = [
            new RegExp(`\\*\\*${filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(Document ID:\\s*${docId}\\)\\*\\*`, 'gi'),
            new RegExp(`${filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(Document ID:\\s*${docId}\\)`, 'gi')
          ];
          
          patterns.forEach(pattern => {
            processedContent = processedContent.replace(pattern, (match) => {
              return `${match}\n\n![${filename}](${window.location.origin}${url})\n\n`;
            });
          });
        }
      });
      return processedContent;
    }
    if (format === 'json') {
      // For JSON, create a structured export
      const jsonExport = {
        conversationId: conversationId,
        requestType: requestType,
        exportDate: new Date().toISOString(),
        content: {
          raw: content,
          plain: stripMarkdown(content)
        },
        documents: []
      };

      // Add document information
      if (documentIds.length > 0) {
        documentIds.forEach(docId => {
          const imageInfo = imageCache && imageCache[docId] ? imageCache[docId] : null;
          const docData = {
            id: docId,
            filename: imageInfo ? imageInfo.filename : 'unknown',
            url: `${window.location.origin}${getImageUrl(docId)}`,
            mimetype: imageInfo ? imageInfo.mimetype : 'unknown'
          };

          // Add PDF pages if available
          if (isPdfWithPages(imageInfo)) {
            docData.pages = imageInfo.pageImages.map(page => ({
              pageNumber: page.pageNumber,
              url: `${window.location.origin}${getPdfPageUrl(docId, page.pageNumber)}`
            }));
          }

          jsonExport.documents.push(docData);
        });
      }

      return JSON.stringify(jsonExport, null, 2);
    }
    

    
    if (format === 'pdf') {
      // For PDF, we'll handle this in the HTML generation
      return { content, documentIds };
    }
    
    return content;
  };

  const handleDownload = async (format) => {
    if (!messageContent) {
      setExportStatus({ type: 'error', message: 'No content to export' });
      return;
    }

    setIsExporting(true);
    setExportStatus({ type: 'loading', message: 'Preparing download...' });

    try {
      // Prepare content based on format
      let contentToExport = processContentWithImages(messageContent, format);
      
      // Generate filename
      const date = new Date().toISOString().split('T')[0];
      const timeStamp = Date.now();
      const filename = `gene-ai-export-${date}-${timeStamp}.${format}`;

      if (format === 'pdf') {
        // Handle PDF export with images
        const { content, documentIds } = contentToExport;
        const printWindow = window.open('', '_blank');
        
        // DEBUG logging
        console.log('🔍 PDF Export Debug:');
        console.log('Document IDs found:', documentIds);
        console.log('Image Cache:', imageCache);
        
        // Convert markdown to HTML
        let htmlContent = content
          .replace(/^# (.*$)/gm, '<h1>$1</h1>')
          .replace(/^## (.*$)/gm, '<h2>$1</h2>')
          .replace(/^### (.*$)/gm, '<h3>$1</h3>')
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/\n/g, '<br>')
          .replace(/^- (.*$)/gm, '<li>$1</li>')
          .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
        
        // Process images and PDFs
        if (imageCache && documentIds.length > 0) {
          console.log('🖼️ Processing images and PDFs for export...');
          
          documentIds.forEach(docId => {
            if (imageCache[docId]) {
              const imageInfo = imageCache[docId];
              
              // Check if it's a PDF with pages
              if (isPdfWithPages(imageInfo)) {
                console.log(`📄 Processing PDF with ${imageInfo.pageImages.length} pages`);
                
                // Replace the Document ID reference with all PDF pages
                const pattern = new RegExp(
                  `\\(Document ID:\\s*${docId}\\)`,
                  'gi'
                );
                
                htmlContent = htmlContent.replace(pattern, (match) => {
                  let pdfPagesHtml = '<br><br>';
                  
                  // Add each page of the PDF
                  imageInfo.pageImages.forEach((page, index) => {
                    const pageUrl = getPdfPageUrl(docId, page.pageNumber);
                    pdfPagesHtml += `
                      <div style="margin: 20px 0; page-break-inside: avoid;">
                        <h4 style="color: #2e7d32; margin-bottom: 10px;">
                          ${imageInfo.originalName} - Page ${page.pageNumber}
                        </h4>
                        <img src="${window.location.origin}${pageUrl}" 
                             alt="${imageInfo.originalName} - Page ${page.pageNumber}" 
                             style="max-width: 100%; height: auto; margin: 10px 0; border: 1px solid #ddd; border-radius: 4px; display: block; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                      </div>
                    `;
                  });
                  
                  return pdfPagesHtml;
                });
              } else {
                // Regular image handling
                const url = getImageUrl(docId);
                const pattern = new RegExp(
                  `\\(Document ID:\\s*${docId}\\)`,
                  'gi'
                );
                
                htmlContent = htmlContent.replace(pattern, (match) => {
                  console.log(`✅ Replacing "${match}" with image`);
                  return `<br><br><img src="${window.location.origin}${url}" alt="${imageInfo.filename}" style="max-width: 100%; height: auto; margin: 10px 0; border: 1px solid #ddd; border-radius: 4px; display: block;">`;
                });
              }
            }
          });
        }

        printWindow.document.write(`
          <html>
            <head>
              <title>Gene Research Export</title>
              <style>
                body { 
                  font-family: Georgia, serif; 
                  line-height: 1.6; 
                  margin: 1in; 
                  max-width: 8.5in;
                  background: white;
                  color: #333;
                }
                h1 { 
                  color: #2e7d32; 
                  border-bottom: 3px solid #2e7d32; 
                  padding-bottom: 10px; 
                  font-size: 24px;
                }
                h2 { 
                  color: #2e7d32; 
                  font-size: 20px;
                  margin-top: 30px;
                }
                h3 { 
                  color: #2e7d32; 
                  font-size: 16px;
                  margin-top: 25px;
                }
                h4 {
                  color: #2e7d32;
                  font-size: 14px;
                  margin-top: 20px;
                }
                p { margin-bottom: 15px; }
                ul { margin: 15px 0; padding-left: 30px; }
                li { margin-bottom: 8px; }
                strong { color: #1a1a1a; }
                img { 
                  max-width: 100%; 
                  height: auto; 
                  margin: 15px 0; 
                  display: block;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                .header { 
                  text-align: center; 
                  padding: 20px; 
                  background: #f8f9fa; 
                  margin-bottom: 30px; 
                  border: 1px solid #dee2e6;
                }
                .footer { 
                  margin-top: 40px; 
                  padding-top: 20px; 
                  border-top: 1px solid #dee2e6; 
                  font-size: 12px; 
                  color: #666;
                  text-align: center;
                }
                @media print {
                  .no-print { display: none; }
                  body { margin: 0.5in; }
                  img { 
                    max-width: 6.5in; 
                    page-break-inside: avoid;
                  }
                }
              </style>
            </head>
            <body>
              <div class="header no-print">
                <h2>Gene Genealogical Research</h2>
                <p>Generated on ${new Date().toLocaleDateString()}</p>
                <button onclick="window.print()" style="padding: 10px 20px; background: #2e7d32; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">Print / Save as PDF</button>
              </div>
              <div class="content">${htmlContent}</div>
              <div class="footer">
                <p>Generated by Gene - Powered Genealogical Research Assistant</p>
                <p>© ${new Date().getFullYear()} GNology</p>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        
        setExportStatus({ type: 'success', message: 'PDF prepared with images and documents' });
      } else {
        // Handle TXT and MD downloads with direct blob download
        let mimeType = 'text/plain';
        if (format === 'md') {
          mimeType = 'text/markdown';
        } else if (format === 'json') {
          mimeType = 'application/json';
        }
        const blob = new Blob([contentToExport], { type: mimeType });
        const url = URL.createObjectURL(blob);
        
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = filename;
        downloadLink.style.display = 'none';
        
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        
        // Clean up the URL object
        URL.revokeObjectURL(url);
        
        setExportStatus({ type: 'success', message: `Downloaded as ${format.toUpperCase()}` });
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportStatus({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        // Clear status after 3 seconds
        setTimeout(() => setExportStatus(null), 3000);
      }, 1000);
    }
  };

  // Determine content size for display
  const getContentSize = () => {
    const charCount = messageContent.length;
    if (charCount > 50000) return { label: '📚 Large response', color: '#b45309' };
    if (charCount > 15000) return { label: '📖 Medium response', color: '#0f766e' };
    return { label: '📝 Response', color: '#6b7280' };
  };

  const contentSize = getContentSize();

  return (
    <div className="download-buttons">
      <div className="download-label">
        <span style={{ color: contentSize.color }}>{contentSize.label}</span> - Download research:
      </div>
      
      <div className="button-group">
        <button 
          onClick={() => handleDownload('txt')}
          disabled={isExporting}
          className="download-btn txt-btn"
          title="Download as plain text (formatting removed)"
        >
          {isExporting ? '...' : 'TXT'}
        </button>
        
        <button 
          onClick={() => handleDownload('md')}
          disabled={isExporting}
          className="download-btn md-btn"
          title="Download as Markdown (formatting preserved)"
        >
          {isExporting ? '...' : 'MD'}
        </button>
        <button 
          onClick={() => handleDownload('json')}
          disabled={isExporting}
          className="download-btn json-btn"
          title="Download as JSON (structured data)"
        >
          {isExporting ? '...' : 'JSON'}
        </button>
        
        <button 
          onClick={() => handleDownload('pdf')}
          disabled={isExporting}
          className="download-btn pdf-btn"
          title="Open print-friendly PDF view"
        >
          {isExporting ? '...' : 'PDF'}
        </button>
      </div>
      
      {exportStatus && (
        <div className={`export-status ${exportStatus.type}`}>
          {exportStatus.message}
        </div>
      )}
    </div>
  );
};

export default DownloadButtons;
